import { CustomDialogConfiguration } from './custom-dialog';
import { EntryType } from './entry-type';
import { ListItem } from './list-item';
import { Security } from './security';

export enum FieldTypes {
  String = 'string',
  Number = 'number',
  DateTime = 'dateTime',
  Selector = 'selector',
  Display = 'display',
}

export type FieldType = `${FieldTypes}`;

export enum LabelPositions {
  Top = 'top',
  Left = 'left',
  None = 'none',
}

export type LabelPosition = `${LabelPositions}`;

export interface Field {
  id: string;
  parentId?: string;
  name: string;
  description?: string;
  placeholder?: string;
  hint?: string;
  readonly: boolean;
  required?: boolean;
  type: FieldType;
  clearButton?: boolean;
  cssClass?: string;
  containerClass?: string;
  containerStyle?: Record<string, string> | null;
  labelPosition?: LabelPosition;
  metadata?: FieldMetadata;
  fields?: FieldSet;
  customDialogConfiguration?: CustomDialogConfiguration;
  errorMessages?: {
    min?: string;
    max?: string;
    custom?: string;
  };
}

export interface FieldMetadata {
  alias?: string[];
  active: boolean;
  assignable: boolean;
  appliesTo: string[];
  entity: string;
  expressions?: {
    value: string;
    required: string;
  };
  folder?: string;
  hasOptions: boolean;
  yesNo: boolean;
  index?: string;
  isCustom: boolean;
  isPredefined: boolean;
  key: string;
  nullable: boolean;
  sortable: boolean;
  queryable?: FieldQueryable;
  tree: string[];
  type: string;
  tag?: string;
}

export interface FieldQueryable {
  equals: boolean;
  notEquals: boolean;
  contains: boolean;
  notContains: boolean;
  exists: boolean;
  notExists: boolean;
  inRange: boolean;
  notInRange: boolean;
  greatThan: boolean;
  greatThanOrEqual: boolean;
  lessThan: boolean;
  lessThanOrEqual: boolean;
  like: boolean;
  notLike: boolean;
}

export interface StringField extends Field {
  type: 'string';
  maxlength?: number;
  lines?: number;
  showAsLink?: boolean;
  linkClick?: (value: string) => void;
}

export interface NumberField extends Field {
  type: 'number';
  min?: number;
  max?: number;
  step?: number;
  showSpinners?: boolean;
  decimals?: number;
  currency?: string;
}

export type DateTimeMode = 'date' | 'time' | 'dateTime';

export interface DateTimeField extends Field {
  type: 'dateTime';
  selection: DateTimeMode;
  min?: Date;
  max?: Date;
  ignoreYear?: boolean;
  inputFormat?: string;
  displayFormat?: string;
}

export type SelectorMode =
  | 'single'
  | 'multiple'
  | 'dialog'
  | 'suggest'
  | 'autoComplete'
  | 'dialogWithMultiple';

export interface SelectorOption<T> extends ListItem<T> {
  isActive?: boolean;
}

export interface SelectorCompletionHandler<T> {
  initialize(selector: T): void;
}

export interface SelectorField<T> extends Field {
  type: 'selector';
  fillMode?: 'solid' | 'flat' | 'outline';
  selection: SelectorMode;
  options: SelectorOption<T>[];
  entity?: EntryType;
  allowEmpty?: boolean;
  maxlength?: number;
  completionHandler?: SelectorCompletionHandler<unknown>;
}

export interface FieldSet {
  [key: string]: Field;
}

export type LayoutContainerType = 'row' | 'column' | 'group';

export type LayoutElementType = LayoutContainerType | 'field' | 'separator';

export interface LayoutElement {
  type: LayoutElementType;
  name?: string;
  field?: string;
  elements?: LayoutElement[];
  properties?: LayoutElementProperties;
}

export interface LayoutElementProperties {
  cssClass?: string;
}

export interface Layout extends LayoutElement {
  key: string;
  parentKey: string;
  id: number;
  description?: string;
  isDefault: boolean;
  appliesTo: string;
  target: string | null;
  secAccess?: {
    read: string;
    write: string;
  };
  secStatus?: Security;
}

export interface Folder {
  id: string;
  name: string;
  index?: string;
  path: string;
  fields: string[];
  children: Folder[];
  appliesTo: string[];
  parent?: Folder;
}

export interface FolderCatalog {
  all: { [key: string]: Folder };
  tree: Folder[];
}

export interface EntityCatalog {
  entity: string;
  fields: FieldSet;
  folders: FolderCatalog;
  layouts: Layout[];
}

export type MetadataCatalog = Record<string, EntityCatalog>;
